import { useState } from "react";
import { DocsComponentDemo } from "../DocsComponentDemo";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { 
  Video, 
  Mic, 
  ArrowRight, 
  Play, 
  Square, 
  CheckCircle, 
  Check,
  ChevronLeft,
  ChevronRight
} from "lucide-react";
import beatviewLogo from "@/assets/beatview-logo.svg";

// Mock interview data
const mockInterview = {
  brandName: "TechFlow Inc.",
  brandColor: "#6366f1",
  role: "Senior Frontend Engineer",
  message: "We're excited to learn more about you!",
  questions: [
    "Tell us about yourself and your experience with frontend development.",
    "Describe a challenging project you've worked on and how you solved it.",
    "How do you approach learning new technologies?",
  ],
  questionDuration: 120,
};

type InterviewStage = "welcome" | "device-test" | "interview" | "complete";

export function DemoInterviewWorkflow() {
  const [currentView, setCurrentView] = useState<InterviewStage>("welcome");
  const [selectedMode, setSelectedMode] = useState<"video" | "audio">("video");
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [isRecording, setIsRecording] = useState(false);
  const [deviceTestPassed, setDeviceTestPassed] = useState(false);

  const brandColor = mockInterview.brandColor;

  const handleNextView = () => {
    if (currentView === "welcome") {
      setCurrentView("device-test");
    } else if (currentView === "device-test") {
      setDeviceTestPassed(true);
      setTimeout(() => setCurrentView("interview"), 500);
    } else if (currentView === "interview") {
      if (isRecording) {
        setIsRecording(false);
        if (currentQuestion < mockInterview.questions.length - 1) {
          setTimeout(() => {
            setCurrentQuestion(prev => prev + 1);
          }, 500);
        } else {
          setTimeout(() => setCurrentView("complete"), 500);
        }
      } else {
        setIsRecording(true);
      }
    }
  };

  const resetDemo = () => {
    setCurrentView("welcome");
    setCurrentQuestion(0);
    setIsRecording(false);
    setDeviceTestPassed(false);
  };

  return (
    <DocsComponentDemo 
      caption="Interactive AI interview workflow - click through to see each stage"
      fullWidth
    >
      <div
        className="rounded-lg overflow-hidden"
        style={{
          background: `linear-gradient(135deg, ${brandColor}08 0%, transparent 50%)`,
        }}
      >
        {/* Header */}
        <header className="border-b border-border/50 bg-background/80 backdrop-blur-sm">
          <div className="px-4 py-3 flex items-center gap-3 justify-center">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-xs font-bold"
              style={{ backgroundColor: brandColor }}
            >
              TF
            </div>
            <div className="text-center">
              <p className="font-semibold text-foreground text-sm">{mockInterview.brandName}</p>
              <p className="text-xs text-muted-foreground">{mockInterview.message}</p>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="p-4">
          {/* Welcome Screen */}
          {currentView === "welcome" && (
            <Card className="max-w-md mx-auto">
              <CardHeader className="text-center pb-2">
                <div
                  className="w-10 h-10 rounded-lg mx-auto mb-2 flex items-center justify-center text-white text-sm font-bold"
                  style={{ backgroundColor: brandColor }}
                >
                  TF
                </div>
                <CardTitle className="text-lg">Welcome to your interview</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div
                  className="p-3 rounded-lg text-center"
                  style={{ backgroundColor: `${brandColor}15` }}
                >
                  <p className="text-sm font-medium" style={{ color: brandColor }}>
                    {mockInterview.role}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    at {mockInterview.brandName}
                  </p>
                </div>

                <div className="space-y-2 text-xs text-muted-foreground">
                  <div className="flex items-start gap-2">
                    <div
                      className="w-5 h-5 rounded-full flex items-center justify-center text-white text-[10px] font-medium shrink-0"
                      style={{ backgroundColor: brandColor }}
                    >
                      1
                    </div>
                    <p>
                      This interview has <strong>{mockInterview.questions.length} questions</strong>. 
                      You'll have up to <strong>{mockInterview.questionDuration} seconds</strong> to answer each.
                    </p>
                  </div>
                  <div className="flex items-start gap-2">
                    <div
                      className="w-5 h-5 rounded-full flex items-center justify-center text-white text-[10px] font-medium shrink-0"
                      style={{ backgroundColor: brandColor }}
                    >
                      2
                    </div>
                    <p>
                      Find a quiet, well-lit space. Make sure your camera and microphone are working.
                    </p>
                  </div>
                  <div className="flex items-start gap-2">
                    <div
                      className="w-5 h-5 rounded-full flex items-center justify-center text-white text-[10px] font-medium shrink-0"
                      style={{ backgroundColor: brandColor }}
                    >
                      3
                    </div>
                    <p>
                      Take a breath and be yourself!
                    </p>
                  </div>
                </div>

                {/* Mode Selection */}
                <div className="space-y-2">
                  <p className="text-xs font-medium">Choose your format:</p>
                  <RadioGroup
                    value={selectedMode}
                    onValueChange={(value) => setSelectedMode(value as "video" | "audio")}
                    className="grid grid-cols-2 gap-3"
                  >
                    <div>
                      <RadioGroupItem value="video" id="demo-video" className="peer sr-only" />
                      <Label
                        htmlFor="demo-video"
                        className="flex flex-col items-center justify-center rounded-lg border-2 p-2 cursor-pointer transition-colors"
                        style={{
                          borderColor: selectedMode === "video" ? brandColor : undefined,
                          backgroundColor: selectedMode === "video" ? `${brandColor}15` : undefined,
                        }}
                      >
                        <Video className="h-4 w-4 mb-1" style={{ color: selectedMode === "video" ? brandColor : undefined }} />
                        <span className="text-xs font-medium">Video</span>
                      </Label>
                    </div>
                    <div>
                      <RadioGroupItem value="audio" id="demo-audio" className="peer sr-only" />
                      <Label
                        htmlFor="demo-audio"
                        className="flex flex-col items-center justify-center rounded-lg border-2 p-2 cursor-pointer transition-colors"
                        style={{
                          borderColor: selectedMode === "audio" ? brandColor : undefined,
                          backgroundColor: selectedMode === "audio" ? `${brandColor}15` : undefined,
                        }}
                      >
                        <Mic className="h-4 w-4 mb-1" style={{ color: selectedMode === "audio" ? brandColor : undefined }} />
                        <span className="text-xs font-medium">Audio</span>
                      </Label>
                    </div>
                  </RadioGroup>
                </div>

                <Button
                  className="w-full text-sm"
                  onClick={handleNextView}
                  style={{ backgroundColor: brandColor }}
                >
                  Test Your Setup
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Device Test Screen */}
          {currentView === "device-test" && (
            <Card className="max-w-md mx-auto">
              <CardHeader className="text-center pb-2">
                <CardTitle className="text-lg">
                  Test Your {selectedMode === "video" ? "Camera &" : ""} Microphone
                </CardTitle>
                <p className="text-xs text-muted-foreground">
                  Make sure everything is working before we begin
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Mock Camera/Audio Preview */}
                <div 
                  className="aspect-video rounded-lg bg-muted flex items-center justify-center"
                  style={{ background: "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)" }}
                >
                  {selectedMode === "video" ? (
                    <div className="text-center text-white">
                      <Video className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p className="text-xs opacity-70">Camera preview would appear here</p>
                    </div>
                  ) : (
                    <div className="flex items-end justify-center gap-1 h-16">
                      {Array.from({ length: 12 }).map((_, i) => (
                        <div
                          key={i}
                          className="w-2 rounded-full animate-pulse"
                          style={{
                            height: `${20 + Math.random() * 40}px`,
                            backgroundColor: brandColor,
                            animationDelay: `${i * 0.1}s`,
                          }}
                        />
                      ))}
                    </div>
                  )}
                </div>

                {/* Test Status */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-xs">
                    <div className={cn(
                      "w-4 h-4 rounded-full flex items-center justify-center",
                      deviceTestPassed ? "bg-green-500" : "bg-muted"
                    )}>
                      {deviceTestPassed && <Check className="h-3 w-3 text-white" />}
                    </div>
                    <span className={deviceTestPassed ? "text-green-600" : "text-muted-foreground"}>
                      {selectedMode === "video" ? "Camera" : "Microphone"} detected
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <div className={cn(
                      "w-4 h-4 rounded-full flex items-center justify-center",
                      deviceTestPassed ? "bg-green-500" : "bg-muted"
                    )}>
                      {deviceTestPassed && <Check className="h-3 w-3 text-white" />}
                    </div>
                    <span className={deviceTestPassed ? "text-green-600" : "text-muted-foreground"}>
                      Audio levels good
                    </span>
                  </div>
                </div>

                <Button
                  className="w-full text-sm"
                  onClick={handleNextView}
                  style={{ backgroundColor: brandColor }}
                >
                  {deviceTestPassed ? "Start Interview" : "Run Test & Continue"}
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Interview Screen */}
          {currentView === "interview" && (
            <div className="max-w-2xl mx-auto grid grid-cols-1 md:grid-cols-5 gap-4">
              {/* Left - Question */}
              <div className="md:col-span-2 space-y-3">
                <div className="flex items-baseline gap-2">
                  <span 
                    className="text-2xl font-bold"
                    style={{ color: brandColor }}
                  >
                    {currentQuestion + 1}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    of {mockInterview.questions.length} questions
                  </span>
                </div>
                
                <Card className="p-3">
                  <p className="text-sm font-medium">
                    {mockInterview.questions[currentQuestion]}
                  </p>
                </Card>

                {/* Progress dots */}
                <div className="flex gap-1.5">
                  {mockInterview.questions.map((_, idx) => (
                    <div
                      key={idx}
                      className={cn(
                        "h-1.5 rounded-full transition-all",
                        idx <= currentQuestion ? "w-6" : "w-1.5"
                      )}
                      style={{
                        backgroundColor: idx <= currentQuestion ? brandColor : undefined,
                      }}
                    />
                  ))}
                </div>
              </div>

              {/* Right - Camera/Recording */}
              <div className="md:col-span-3">
                <div className="relative rounded-xl overflow-hidden bg-black">
                  {/* Mock Camera View */}
                  <div 
                    className="aspect-video flex items-center justify-center"
                    style={{ background: "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)" }}
                  >
                    {selectedMode === "video" ? (
                      <Video className="h-12 w-12 text-white/30" />
                    ) : (
                      <div className="flex items-end justify-center gap-1 h-16">
                        {Array.from({ length: 20 }).map((_, i) => (
                          <div
                            key={i}
                            className={cn(
                              "w-1.5 rounded-full transition-all",
                              isRecording && "animate-pulse"
                            )}
                            style={{
                              height: `${10 + Math.random() * 50}px`,
                              backgroundColor: isRecording ? brandColor : "rgba(255,255,255,0.2)",
                              animationDelay: `${i * 0.05}s`,
                            }}
                          />
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Recording Indicator */}
                  {isRecording && (
                    <div className="absolute top-3 left-3 flex items-center gap-2 bg-black/70 rounded-lg px-2 py-1">
                      <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75" />
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
                      </span>
                      <span className="text-white text-xs font-medium">REC</span>
                    </div>
                  )}

                  {/* Countdown Timer (when not recording) */}
                  {!isRecording && (
                    <div className="absolute top-3 right-3 bg-black/70 rounded-lg px-2 py-1">
                      <span className="text-white text-xs font-mono">Auto-start: 0:20</span>
                    </div>
                  )}

                  {/* Controls Bar */}
                  <div className="absolute bottom-0 inset-x-0 p-3 bg-gradient-to-t from-black/80 to-transparent">
                    <div className="flex justify-center">
                      <Button
                        onClick={handleNextView}
                        className={cn(
                          "min-w-[160px] text-sm font-semibold",
                          isRecording && "bg-red-500 hover:bg-red-600"
                        )}
                        style={{ 
                          backgroundColor: !isRecording ? brandColor : undefined 
                        }}
                      >
                        {isRecording ? (
                          <>
                            <Square className="h-4 w-4 mr-2" />
                            Stop & Submit
                          </>
                        ) : (
                          <>
                            <Play className="h-4 w-4 mr-2" />
                            Start Answering
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Complete Screen */}
          {currentView === "complete" && (
            <Card className="text-center max-w-md mx-auto p-8">
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                style={{ backgroundColor: `${brandColor}15` }}
              >
                <CheckCircle className="h-8 w-8" style={{ color: brandColor }} />
              </div>
              <h2 className="text-xl font-semibold mb-2">Interview Complete!</h2>
              <p className="text-sm text-muted-foreground mb-4">
                Thank you for completing your interview for the{" "}
                <strong>{mockInterview.role}</strong> position at{" "}
                <strong>{mockInterview.brandName}</strong>.
              </p>
              <p className="text-xs text-muted-foreground mb-6">
                We'll review your responses and get back to you soon.
              </p>
              <Button variant="outline" size="sm" onClick={resetDemo}>
                Restart Demo
              </Button>
            </Card>
          )}
        </main>

        {/* Footer */}
        <footer className="border-t border-border/50 py-2">
          <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
            <span>Powered by</span>
            <img src={beatviewLogo} alt="Beatview" className="h-4" />
          </div>
        </footer>

        {/* Navigation Hint */}
        <div className="bg-muted/50 px-4 py-2 text-center text-xs text-muted-foreground flex items-center justify-center gap-4">
          <span className="font-medium">Demo Controls:</span>
          <div className="flex items-center gap-2">
            <span>Current stage: <strong className="capitalize">{currentView}</strong></span>
            {currentView === "interview" && (
              <span>| Question {currentQuestion + 1}/{mockInterview.questions.length}</span>
            )}
          </div>
        </div>
      </div>
    </DocsComponentDemo>
  );
}
